import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

class ExportService {
  // Format date for display
  formatDate(dateString) {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  // Fixed formatTime function - same as Overview.jsx
  formatTime(timeString) {
    if (!timeString) return 'Not specified';
    
    try {
      // Handle different time formats
      let timeToFormat = timeString;
      
      // If it's already in HH:MM format, use it directly
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        timeToFormat = timeString;
      }
      // If it contains AM/PM, extract just the time part
      else if (timeString.includes('AM') || timeString.includes('PM')) {
        timeToFormat = timeString.replace(/\s*(AM|PM)/i, '').trim();
      }
      
      // Parse the time
      const [hours, minutes] = timeToFormat.split(':');
      const hour24 = parseInt(hours, 10);
      const min = parseInt(minutes, 10);
      
      // Validate the time
      if (isNaN(hour24) || isNaN(min) || hour24 < 0 || hour24 > 23 || min < 0 || min > 59) {
        return timeString; // Return original if invalid
      }
      
      // Convert to 12-hour format
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      const formattedMinutes = min.toString().padStart(2, '0');
      
      return `${hour12}:${formattedMinutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return timeString; // Return original string if formatting fails
    }
  }

  // Get filtered appointments based on date range
  getFilteredAppointments(appointments, filterType, customStartDate = null, customEndDate = null) {
    if (!appointments || appointments.length === 0) return [];

    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    let filtered = [...appointments];

    switch (filterType) {
      case 'today':
        filtered = appointments.filter(appointment => 
          appointment.appointment_date === todayString
        );
        break;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const startOfWeekString = startOfWeek.toISOString().split('T')[0];
        
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        const endOfWeekString = endOfWeek.toISOString().split('T')[0];

        filtered = appointments.filter(appointment => {
          const appointmentDate = appointment.appointment_date;
          return appointmentDate >= startOfWeekString && appointmentDate <= endOfWeekString;
        });
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          filtered = appointments.filter(appointment => {
            const appointmentDate = appointment.appointment_date;
            return appointmentDate >= customStartDate && appointmentDate <= customEndDate;
          });
        }
        break;
      default:
        // Return all appointments
        break;
    }

    // Sort appointments in descending order by date and time
    return filtered.sort((a, b) => {
      const dateA = new Date(`${a.appointment_date}T${a.appointment_time || '00:00'}`);
      const dateB = new Date(`${b.appointment_date}T${b.appointment_time || '00:00'}`);
      return dateB - dateA; // Descending order
    });
  }

  // Export to Excel
  exportToExcel(appointments, filterType = 'all', customStartDate = null, customEndDate = null) {
    try {
      const filteredAppointments = this.getFilteredAppointments(appointments, filterType, customStartDate, customEndDate);
      
      if (filteredAppointments.length === 0) {
        throw new Error('No appointments found for the selected date range');
      }

      // Prepare data for Excel
      const excelData = filteredAppointments.map((appointment, index) => ({
        'Sr. No.': index + 1,
        'Booking ID': appointment.booking_id || 'N/A',
        'Patient Name': appointment.patient_name || 'N/A',
        'Gender': appointment.patient_gender || 'N/A',
        'Email': appointment.patient_email || 'N/A',
        'Phone': appointment.patient_phone || 'N/A',
        'Appointment Date': this.formatDate(appointment.appointment_date),
        'Appointment Time': this.formatTime(appointment.appointment_time),
        'Appointment Type': appointment.appointment_type_name || 'N/A',
        'Status': appointment.status || 'Pending',
        'Consultation Fee': appointment.consultation_fee ? `₹${appointment.consultation_fee}` : 'N/A',
        'Notes': appointment.notes || 'N/A',
        'Created At': appointment.created_at ? new Date(appointment.created_at).toLocaleDateString() : 'N/A'
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // Sr. No.
        { wch: 15 },  // Booking ID
        { wch: 20 },  // Patient Name
        { wch: 8 },   // Gender
        { wch: 25 },  // Email
        { wch: 15 },  // Phone
        { wch: 18 },  // Appointment Date
        { wch: 15 },  // Appointment Time
        { wch: 20 },  // Appointment Type
        { wch: 12 },  // Status
        { wch: 15 },  // Consultation Fee
        { wch: 30 },  // Notes
        { wch: 15 }   // Created At
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Appointments');

      // Generate filename
      const dateRange = this.getDateRangeLabel(filterType, customStartDate, customEndDate);
      const filename = `Best_Podiatrist_Appointments_${dateRange}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      return {
        success: true,
        message: `Excel file exported successfully: ${filename}`,
        count: filteredAppointments.length
      };
    } catch (error) {
      console.error('Excel export error:', error);
      return {
        success: false,
        message: error.message || 'Failed to export Excel file'
      };
    }
  }

  // Export to PDF
  exportToPDF(appointments, filterType = 'all', customStartDate = null, customEndDate = null) {
    try {
      const filteredAppointments = this.getFilteredAppointments(appointments, filterType, customStartDate, customEndDate);
      
      if (filteredAppointments.length === 0) {
        throw new Error('No appointments found for the selected date range');
      }

      // Create new PDF document
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

      // Add header
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('Best Podiatrist - Dr. Amit Kumar C Jain', 20, 20);
      
      doc.setFontSize(14);
      doc.setTextColor(60, 60, 60);
      doc.text('Diabetic Foot Surgery Practice', 20, 30);

      // Add date range and export info
      const dateRange = this.getDateRangeLabel(filterType, customStartDate, customEndDate);
      doc.setFontSize(12);
      doc.text(`Appointment Report - ${dateRange}`, 20, 40);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`, 20, 48);
      doc.text(`Total Appointments: ${filteredAppointments.length}`, 20, 56);

      // Prepare table data
      const tableData = filteredAppointments.map((appointment, index) => [
        index + 1,
        appointment.booking_id || 'N/A',
        appointment.patient_name || 'N/A',
        appointment.patient_gender || 'N/A',
        this.formatDate(appointment.appointment_date),
        this.formatTime(appointment.appointment_time),
        appointment.appointment_type_name || 'N/A',
        appointment.status || 'Pending',
        appointment.consultation_fee ? `₹${appointment.consultation_fee}` : 'N/A'
      ]);

      // Add table
      doc.autoTable({
        startY: 65,
        head: [['Sr.', 'Booking ID', 'Patient Name', 'Gender', 'Date', 'Time', 'Type', 'Status', 'Fee']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246], // Blue color
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: 60
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252] // Light gray
        },
        columnStyles: {
          0: { cellWidth: 12 },  // Sr.
          1: { cellWidth: 25 },  // Booking ID
          2: { cellWidth: 35 },  // Patient Name
          3: { cellWidth: 20 },  // Gender
          4: { cellWidth: 30 },  // Date
          5: { cellWidth: 25 },  // Time
          6: { cellWidth: 35 },  // Type
          7: { cellWidth: 20 },  // Status
          8: { cellWidth: 20 }   // Fee
        },
        margin: { left: 20, right: 20 },
        didDrawPage: (data) => {
          // Add page numbers
          const pageNumber = doc.internal.getNumberOfPages();
          const pageSize = doc.internal.pageSize;
          doc.setFontSize(10);
          doc.setTextColor(128, 128, 128);
          doc.text(
            `Page ${data.pageNumber} of ${pageNumber}`,
            pageSize.width - 40,
            pageSize.height - 10
          );
        }
      });

      // Add footer with additional info if there's space
      const finalY = doc.lastAutoTable.finalY || 65;
      if (finalY < 180) {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('This report contains confidential patient information.', 20, finalY + 20);
        doc.text('Please handle according to HIPAA guidelines and medical privacy regulations.', 20, finalY + 28);
      }

      // Generate filename and save
      const filename = `Best_Podiatrist_Appointments_${dateRange}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      return {
        success: true,
        message: `PDF file exported successfully: ${filename}`,
        count: filteredAppointments.length
      };
    } catch (error) {
      console.error('PDF export error:', error);
      return {
        success: false,
        message: error.message || 'Failed to export PDF file'
      };
    }
  }

  // Get date range label for filename and display
  getDateRangeLabel(filterType, customStartDate = null, customEndDate = null) {
    const today = new Date();
    
    switch (filterType) {
      case 'today':
        return `Today_${today.toISOString().split('T')[0]}`;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        return `Week_${startOfWeek.toISOString().split('T')[0]}_to_${endOfWeek.toISOString().split('T')[0]}`;
      case 'custom':
        if (customStartDate && customEndDate) {
          return `Custom_${customStartDate}_to_${customEndDate}`;
        }
        return 'Custom_Range';
      default:
        return 'All_Appointments';
    }
  }

  // Get date range display text
  getDateRangeDisplayText(filterType, customStartDate = null, customEndDate = null) {
    const today = new Date();
    
    switch (filterType) {
      case 'today':
        return `Today (${today.toLocaleDateString()})`;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(today);
        endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        return `This Week (${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()})`;
      case 'custom':
        if (customStartDate && customEndDate) {
          return `Custom Range (${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()})`;
        }
        return 'Custom Date Range';
      default:
        return 'All Appointments';
    }
  }
}

export const exportService = new ExportService();